import { useState, useMemo, useEffect } from 'react';
import { BottomSheet } from '@/components/base/BottomSheet';
import { showToast } from '@/components/base/Toast';
import {
  getDebtsByFarmer, getFarmerById, getActivePaymentIntentByLoan,
  createRepaymentIntent, confirmTransfer, getLoansByFarmer, getFarmerInterestRateOverride,
} from '@/store';
import { useStore } from '@/hooks/useStore';
import { formatCurrencyCompact, formatCurrencyFull } from '@/utils/formatters';
import { estimateAmountOwed, resolveEffectiveRatePercent } from '@/utils/interest';
import type { Debt, PaymentIntent, PaymentIntentStatus } from '@/types';

// The real backend settles a loan in one shot (no partial repayment tracking) —
// this is a client-side ESTIMATE only, shown before a PaymentIntent exists.
// Once one is created, the server-authoritative createdPI.soTien (which already
// includes gốc+lãi) is what's actually shown/transferred — see handleSelectDebt.
// This estimate skips the HTX-scoped rate override (farmer-facing, no htxId in
// scope here) — a small precision gap only on the pre-intent list screen.
function getTotalToPay(debt: Debt, loans: ReturnType<typeof getLoansByFarmer>): number {
  const loan = loans.find((l) => l.id === (debt.advanceRequestId || debt.id));
  if (!debt.ngayVay || loan?.laiSuat === undefined) return debt.conNo;
  const farmerRateOverride = getFarmerInterestRateOverride(debt.farmerId);
  const effectiveRate = resolveEffectiveRatePercent(loan.laiSuat, farmerRateOverride, undefined);
  const { accruedVnd } = estimateAmountOwed(debt.soTien, effectiveRate, debt.ngayVay, loan.ngayTatToan);
  return debt.conNo + accruedVnd;
}

interface RepayDebtSheetProps {
  open: boolean;
  onClose: () => void;
  farmerId: string;
}

type Step = 'list' | 'qr' | 'confirm' | 'result';

const PI_STATUS_LABEL: Record<PaymentIntentStatus, string> = {
  qr_created: 'Đã tạo QR',
  farmer_confirmed_transfer: 'Đã xác nhận chuyển khoản',
  waiting_bank_credit: 'Trạng thái cũ',
  bank_credit_received: 'Trạng thái cũ',
  reconciled: 'Đã ghi nhận',
  waiting_htx_confirmation: 'Chờ HTX xác nhận',
  settled: 'Đã tất toán',
  reconcile_failed: 'Đối soát lỗi',
  cancelled: 'Đã hủy',
  complaint: 'Khiếu nại thanh toán',
};

const PI_STATUS_COLOR: Record<PaymentIntentStatus, string> = {
  qr_created: 'bg-sky-50 text-sky-700 border-sky-200',
  farmer_confirmed_transfer: 'bg-amber-50 text-amber-700 border-amber-200',
  waiting_bank_credit: 'bg-orange-50 text-orange-700 border-orange-200',
  bank_credit_received: 'bg-blue-50 text-blue-700 border-blue-200',
  reconciled: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  waiting_htx_confirmation: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  settled: 'bg-agrida-50 text-agrida-700 border-agrida-200',
  reconcile_failed: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
  complaint: 'bg-rose-50 text-rose-700 border-rose-200',
};

const PI_STEPS: PaymentIntentStatus[] = [
  'qr_created',
  'waiting_htx_confirmation',
  'settled',
];

const STEP_ICON: Record<PaymentIntentStatus, string> = {
  qr_created: 'ri-qr-code-line',
  farmer_confirmed_transfer: 'ri-check-double-line',
  waiting_bank_credit: 'ri-time-line',
  bank_credit_received: 'ri-bank-line',
  reconciled: 'ri-check-line',
  waiting_htx_confirmation: 'ri-user-star-line',
  settled: 'ri-check-double-fill',
  reconcile_failed: 'ri-error-warning-line',
  cancelled: 'ri-close-circle-line',
  complaint: 'ri-alert-line',
};

export function RepayDebtSheet({ open, onClose, farmerId }: RepayDebtSheetProps) {
  useStore();
  const [step, setStep] = useState<Step>('list');
  const [selectedDebt, setSelectedDebt] = useState<Debt | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdPI, setCreatedPI] = useState<PaymentIntent | null>(null);

  const farmerName = getFarmerById(farmerId)?.name || farmerId;
  const loans = getLoansByFarmer(farmerId);

  // Read on every render (useStore re-renders when the cache fills). Memoised
  // on farmerId alone, it captured the empty list from before the debts had
  // loaded — the sheet is mounted with the page — and kept showing "Không có
  // khoản nợ nào" to a farmer who did owe.
  const allDebts = getDebtsByFarmer(farmerId);
  const debts = useMemo(() => allDebts.filter((d) => d.status !== 'Đã tất toán' && d.conNo > 0), [allDebts]);

  const sortedDebts = useMemo(() => {
    const priority: Record<string, number> = {
      'Quá hạn': 0, 'Đến hạn': 1, 'Sắp đến hạn': 2,
      'Đang vay': 5, 'Chờ thanh toán': 3, 'Chờ xử lý lỗi thanh toán': 4,
    };
    return [...debts].sort((a, b) => (priority[a.status] ?? 9) - (priority[b.status] ?? 9));
  }, [debts]);

  const handleSelectDebt = async (debt: Debt) => {
    // Check for an existing active PaymentIntent for this loan
    const existingPI = getActivePaymentIntentByLoan(farmerId, debt.advanceRequestId || debt.id);
    if (existingPI && existingPI.status !== 'settled' && existingPI.status !== 'cancelled') {
      setCreatedPI(existingPI);
      setSelectedDebt(debt);
      // A PI created earlier (even in a prior session) may already be past the QR
      // step — jump straight to the result/progress view instead of re-showing the QR.
      setStep(existingPI.status === 'qr_created' ? 'qr' : 'result');
      return;
    }
    // The bank-transfer memo the farmer must type is server-generated
    // (vietQrReconciliationCode) — create the PaymentIntent up front so the QR
    // screen can show the real memo instead of a client-invented placeholder.
    setSelectedDebt(debt);
    setSubmitting(true);
    try {
      const loanId = debt.advanceRequestId || debt.id;
      const intent = await createRepaymentIntent({ farmerId, loanId, amountVnd: getTotalToPay(debt, loans) });
      setCreatedPI(intent);
      setStep('qr');
    } catch {
      showToast('Không thể tạo yêu cầu tất toán, vui lòng thử lại', 'error');
      setSelectedDebt(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToConfirm = () => {
    if (!selectedDebt) return;
    setStep('confirm');
  };

  const handleConfirmTransfer = async () => {
    if (!selectedDebt || !createdPI) return;
    setSubmitting(true);
    try {
      const confirmed = await confirmTransfer(createdPI.id, farmerId);
      setCreatedPI(confirmed);
      setStep('result');
      showToast('Đã gửi xác nhận trả nợ trực tiếp cho HTX.', 'success');
    } catch {
      showToast('Không thể gửi xác nhận, vui lòng thử lại', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('list');
    setSelectedDebt(null);
    setCreatedPI(null);
    setSubmitting(false);
    onClose();
  };

  const getSheetTitle = () => {
    switch (step) {
      case 'qr': return 'QR Tất toán';
      case 'confirm': return 'Xác nhận đã chuyển khoản';
      case 'result': return 'Kết quả';
      default: return 'Tất toán khoản vay';
    }
  };

  const renderPIStatusFlow = (pi: PaymentIntent) => {
    const currentIdx = PI_STEPS.indexOf(pi.status);
    const isFailed = pi.status === 'reconcile_failed';
    const isCancelled = pi.status === 'cancelled';
    const isComplaint = pi.status === 'complaint';

    return (
      <div className="space-y-1">
        {PI_STEPS.map((s, i) => {
          const done = currentIdx >= 0 && i <= currentIdx;
          const current = i === currentIdx;
          const failedStep = isFailed && (s === 'waiting_bank_credit' || s === 'reconciled');
          return (
            <div key={s} className="flex items-center gap-2">
              <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] ${
                done && !failedStep ? 'bg-agrida-500 text-white' :
                failedStep ? 'bg-red-400 text-white' :
                current ? 'bg-amber-500 text-white' :
                'bg-gray-200 text-gray-400'
              }`}>
                <i className={`${STEP_ICON[s]} text-xs`} />
              </span>
              <span className={`text-[11px] ${
                done && !failedStep ? 'text-agrida-700 font-medium' :
                failedStep ? 'text-red-600 font-medium' :
                current ? 'text-amber-700 font-medium' :
                'text-gray-400'
              }`}>
                {PI_STATUS_LABEL[s]}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <BottomSheet open={open} onClose={handleClose} title={getSheetTitle()}>
      {/* ---- STEP: LIST ---- */}
      {step === 'list' && (
        <div className="space-y-2">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2">
            <p className="text-[10px] text-amber-700">
              <strong>Lưu ý quan trọng:</strong> Hệ thống chỉ hỗ trợ <strong>tất toán một lần</strong> toàn bộ khoản vay (gốc + lãi). Nông hộ gửi xác nhận thanh toán trực tiếp cho HTX; khoản vay chỉ tất toán sau khi HTX xác nhận đã nhận đủ tiền.
            </p>
          </div>
          {sortedDebts.length === 0 ? (
            <div className="text-center py-8">
              <span className="w-12 h-12 mx-auto flex items-center justify-center text-agrida-300 mb-2">
                <i className="ri-check-double-line text-3xl" />
              </span>
              <p className="text-sm text-gray-500">Không có khoản nợ nào</p>
              <p className="text-[11px] text-gray-400 mt-1">Tất cả các khoản vay đã được tất toán</p>
            </div>
          ) : (
            <>
              <p className="text-[11px] text-gray-400 mb-1">Chọn khoản vay để tất toán qua QR</p>
              {sortedDebts.map((debt) => {
                const tongPhaiTra = getTotalToPay(debt, loans);
                const existingPI = getActivePaymentIntentByLoan(farmerId, debt.advanceRequestId || debt.id);
                const hasActivePI = existingPI && existingPI.status !== 'settled' && existingPI.status !== 'cancelled';
                const piStatus = existingPI?.status;

                return (
                  <button
                    key={debt.id}
                    type="button"
                    disabled={submitting}
                    onClick={() => handleSelectDebt(debt)}
                    className={`w-full rounded-xl p-3.5 text-left transition-colors border disabled:opacity-60 ${
                      hasActivePI
                        ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                        : 'bg-gray-50 hover:bg-rose-50 border border-transparent hover:border-rose-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-semibold text-gray-900 truncate">{debt.maKhoanVay || debt.id}</span>
                        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${
                          debt.status === 'Quá hạn' ? 'bg-red-50 text-red-700 border-red-200' :
                          debt.status === 'Đến hạn' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-gray-50 text-gray-600 border-gray-200'
                        }`}>
                          {debt.status}
                        </span>
                        {hasActivePI && (
                          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${PI_STATUS_COLOR[piStatus!]}`}>
                            {PI_STATUS_LABEL[piStatus!]}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-bold text-rose-600 shrink-0 ml-2 whitespace-nowrap">
                        {formatCurrencyCompact(tongPhaiTra)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-gray-400">
                      <span>Hạn: {debt.ngayDenHan}</span>
                      <span>· Gốc: {formatCurrencyCompact(debt.soTien)}</span>
                    </div>
                    {debt.status === 'Quá hạn' && (
                      <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                        <i className="ri-alert-line" /> Khoản vay đã quá hạn, cần tất toán sớm
                      </p>
                    )}
                    {hasActivePI && (
                      <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                        <i className="ri-time-line" /> Đã có yêu cầu tất toán đang xử lý — nhấn để xem trạng thái
                      </p>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ---- STEP: QR ---- */}
      {step === 'qr' && selectedDebt && createdPI && (
        <div className="space-y-4 text-center">
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="text-xs text-gray-500 shrink-0">Khoản vay</span>
              <span className="text-xs font-semibold text-gray-900 text-right break-words max-w-[60%]">{selectedDebt.maKhoanVay || selectedDebt.id}</span>
            </div>
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="text-xs text-gray-500 shrink-0">Gốc còn nợ</span>
              <span className="text-sm font-bold text-gray-900 text-right break-words max-w-[60%]">{formatCurrencyFull(createdPI.goc ?? selectedDebt.soTien)}</span>
            </div>
            {!!createdPI.laiTichLuy && (
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-xs text-gray-500 shrink-0">Lãi tích lũy{createdPI.laiSuatApDung ? ` (${createdPI.laiSuatApDung}%/năm)` : ''}</span>
                <span className="text-sm font-bold text-amber-600 text-right break-words max-w-[60%]">{formatCurrencyFull(createdPI.laiTichLuy)}</span>
              </div>
            )}
            <div className="flex items-start justify-between gap-2 pt-2 border-t border-gray-200">
              <span className="text-xs text-gray-500 shrink-0">Tổng tất toán</span>
              {/* Server-authoritative — this is exactly what settlement will
                  record, not the pre-intent client estimate shown on the list. */}
              <span className="text-base font-bold text-rose-600 text-right break-words max-w-[60%]">{formatCurrencyFull(createdPI.soTien)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-[11px] font-medium text-gray-500">Quét mã QR để thanh toán số tiền trên</p>
            <div className="mx-auto w-44 h-44 bg-white border-2 border-slate-800 rounded-xl flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 grid grid-cols-10 grid-rows-10 gap-0.5 p-3">
                {Array.from({ length: 100 }).map((_, i) => (
                  <div key={i} className={`rounded-sm ${Math.random() > 0.55 ? 'bg-slate-800' : 'bg-transparent'}`} />
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <i className="ri-qr-code-line text-slate-700 text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-3 space-y-1.5 text-left">
              <p className="text-[10px] text-slate-300 uppercase tracking-wide font-medium mb-1">Nội dung chuyển khoản bắt buộc</p>
              <div className="bg-slate-900 rounded-xl p-2.5">
                <p className="text-sm font-mono text-agrida-400 font-bold tracking-wider text-center">
                  {createdPI.noiDungChuyenKhoan}
                </p>
              </div>
              <p className="text-[9px] text-amber-400 mt-1 flex items-center gap-1">
                <i className="ri-error-warning-line" /> Phải nhập chính xác nội dung này khi chuyển khoản
              </p>
            </div>

            <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 space-y-1 text-left">
              <div className="flex items-start justify-between gap-2">
                <span className="text-[10px] text-sky-500 shrink-0">Đơn vị nhận</span>
                <span className="text-[10px] font-medium text-sky-700 text-right break-words max-w-[60%]">Quỹ HTX Nông nghiệp Hòa Phát</span>
              </div>
              <div className="flex items-start justify-between gap-2">
                <span className="text-[10px] text-sky-500 shrink-0">Chủ TK</span>
                <span className="text-[10px] font-medium text-sky-700 text-right break-words max-w-[60%]">HTX NÔNG NGHIỆP HÒA PHÁT</span>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-[10px] text-red-700">
                <strong>QUAN TRỌNG:</strong> Bấm "Tôi đã chuyển khoản" không đồng nghĩa với đã tất toán. HTX phải xác nhận đã nhận đủ số tiền trước khi đóng khoản vay.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStep('list')}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              ← Quay lại
            </button>
            <button
              type="button"
              onClick={handleGoToConfirm}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-agrida-600 text-white hover:bg-agrida-700 transition-colors whitespace-nowrap"
            >
              Tôi đã chuyển khoản
            </button>
          </div>
        </div>
      )}

      {/* ---- STEP: CONFIRM ---- */}
      {step === 'confirm' && selectedDebt && createdPI && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-3.5 space-y-1.5">
            <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Thông tin tất toán</h4>
            <div className="flex items-start justify-between gap-2">
              <span className="text-[10px] text-gray-400 shrink-0">Mã khoản vay</span>
              <span className="text-xs font-semibold text-gray-900 text-right break-words max-w-[60%]">{selectedDebt.maKhoanVay || selectedDebt.id}</span>
            </div>
            <div className="flex items-start justify-between gap-2">
              <span className="text-[10px] text-gray-400 shrink-0">Số tiền tất toán</span>
              <span className="text-sm font-bold text-rose-600 text-right break-words max-w-[60%]">{formatCurrencyFull(createdPI.soTien)}</span>
            </div>
            <div className="flex items-start justify-between gap-2">
              <span className="text-[10px] text-gray-400 shrink-0">Nội dung CK</span>
              <span className="text-[10px] font-mono font-medium text-gray-700 text-right break-words max-w-[60%]">
                {createdPI.noiDungChuyenKhoan}
              </span>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-[10px] text-red-700">
              <strong>Xác nhận cuối cùng:</strong> Bạn đã thanh toán đúng số tiền và nội dung cho HTX chưa? Sau bước này, HTX sẽ kiểm tra và xác nhận. Khoản vay chưa được tất toán ngay.
            </p>
          </div>

          <div className="bg-slate-50 rounded-xl p-3 space-y-2">
            <h4 className="text-[10px] font-semibold text-slate-500 uppercase">Quy trình sau khi xác nhận</h4>
            {[
              { icon: 'ri-check-double-line', text: 'Bạn xác nhận đã chuyển khoản', color: 'text-agrida-600' },
              { icon: 'ri-time-line', text: 'HTX kiểm tra khoản tiền đã nhận', color: 'text-amber-600' },
              { icon: 'ri-check-line', text: 'HTX đối chiếu mã khoản vay', color: 'text-sky-600' },
              { icon: 'ri-shield-check-line', text: 'HTX xác nhận → Tất toán khoản vay', color: 'text-indigo-600' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`w-5 h-5 flex items-center justify-center ${item.color}`}>
                  <i className={`${item.icon} text-xs`} />
                </span>
                <span className="text-[10px] text-gray-600">{item.text}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStep('qr')}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              ← Quay lại
            </button>
            <button
              type="button"
              onClick={handleConfirmTransfer}
              disabled={submitting}
              className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold transition-colors whitespace-nowrap ${
                !submitting ? 'bg-agrida-600 text-white hover:bg-agrida-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-1">
                  <span className="w-4 h-4 inline-flex items-center justify-center animate-spin">
                    <i className="ri-loader-4-line text-xs" />
                  </span>
                  Đang gửi...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1">
                  <span className="w-4 h-4 inline-flex items-center justify-center">
                    <i className="ri-check-double-line text-xs" />
                  </span>
                  Xác nhận đã chuyển khoản
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ---- STEP: RESULT ---- */}
      {step === 'result' && createdPI && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="text-xs text-gray-500 shrink-0">PaymentIntent</span>
              <span className="text-xs font-mono text-gray-700 text-right break-words max-w-[60%]">{createdPI.id}</span>
            </div>
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="text-xs text-gray-500 shrink-0">Khoản vay</span>
              <span className="text-xs font-semibold text-gray-900 text-right break-words max-w-[60%]">{createdPI.maKhoanVay || createdPI.debtId}</span>
            </div>
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs text-gray-500 shrink-0">Số tiền tất toán</span>
              <span className="text-base font-bold text-rose-600 text-right break-words max-w-[60%]">{formatCurrencyFull(createdPI.soTien)}</span>
            </div>
          </div>

          <div className={`rounded-xl p-3 border ${PI_STATUS_COLOR[createdPI.status]}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center ${PI_STATUS_COLOR[createdPI.status]}`}>
                <i className={`${STEP_ICON[createdPI.status]} text-base`} />
              </span>
              <span className="text-sm font-semibold">{PI_STATUS_LABEL[createdPI.status]}</span>
            </div>

            {createdPI.status === 'farmer_confirmed_transfer' && (
              <div className="space-y-2">
                <p className="text-[10px] opacity-70">
                  Xác nhận của bạn đã được gửi trực tiếp đến HTX. Bạn sẽ nhận thông báo sau khi HTX kiểm tra và tất toán.
                </p>
                <div className="bg-white/50 rounded-xl p-2.5">
                  <p className="text-[10px] font-mono text-slate-700">
                    Nội dung CK: {createdPI.noiDungChuyenKhoan}
                  </p>
                </div>
              </div>
            )}

            {createdPI.status === 'settled' && (
              <p className="text-[10px] opacity-70">
                Khoản vay đã được tất toán thành công. Hạn mức của bạn đã được phục hồi.
              </p>
            )}

            {createdPI.status === 'reconcile_failed' && (
              <div className="space-y-2">
                <p className="text-[10px] opacity-70">
                  Đối soát không khớp. Có thể do sai số tiền, sai nội dung chuyển khoản, hoặc giao dịch chưa về.
                </p>
                {createdPI.reconcileError && (
                  <div className="bg-white/50 rounded-xl p-2.5">
                    <p className="text-[10px] text-red-700"><strong>Lỗi:</strong> {createdPI.reconcileError}</p>
                  </div>
                )}
              </div>
            )}

            {/* Progress flow */}
            {createdPI.status !== 'settled' && (
              <div className="mt-3 pt-3 border-t border-current/10">
                {renderPIStatusFlow(createdPI)}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="w-full px-4 py-2.5 rounded-xl text-xs font-bold bg-agrida-600 text-white hover:bg-agrida-700 transition-colors whitespace-nowrap"
          >
            Đóng
          </button>
        </div>
      )}
    </BottomSheet>
  );
}
